import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { 
  ArrowLeft, 
  ArrowRight, 
  Copy, 
  CheckCircle, 
  MessageCircle, 
  Lightbulb,
  Save,
  RefreshCw,
  FileText,
  Target,
  Brain,
  Edit3,
  Plus,
  X
} from 'lucide-react';

const QuestionSetGenerator = () => {
  const navigate = useNavigate();
  const { currentUser, familyUsers, family } = useFamilyContext();
  const { updateMasterStrategy } = useCollegeContext();
  
  const [loading, setLoading] = useState(true);
  const [contextSummary, setContextSummary] = useState('');
  const [refinementOptions, setRefinementOptions] = useState([]);
  const [questionSet, setQuestionSet] = useState([]);
  const [questionRefinementOptions, setQuestionRefinementOptions] = useState([]);
  const [copySuccess, setCopySuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedStudentUserId, setSelectedStudentUserId] = useState(
    localStorage.getItem('selectedStudentUserId') || null
  );
  const [studentProfile, setStudentProfile] = useState(null);
  const [parentObservations, setParentObservations] = useState({});
  const [parentBObservations, setParentBObservations] = useState({});
  const [personalityProfile, setPersonalityProfile] = useState({});
  const [academicFocus, setAcademicFocus] = useState({});
  const [bulkPasteText, setBulkPasteText] = useState('');
  const [showBulkPaste, setShowBulkPaste] = useState(false);

  useEffect(() => {
    loadPhase2Data();
  }, [currentUser, familyUsers, family]);

  const loadPhase2Data = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      // Get the selected student (same logic as StudentContextGenerator)
      const savedStudentId = localStorage.getItem('college-selected-student-id');
      let studentUserId = null;
      
      if (savedStudentId) {
        // The savedStudentId is now the auth_user_id, so we can use it directly
        // But we still need to find the student object for name/display purposes
        let student = null;
        if (familyUsers?.length > 0) {
          student = familyUsers.find(u => 
            (u.authUserId || u.auth_user_id) === savedStudentId
          );
        } else if (family?.kids?.length > 0) {
          student = family.kids.find(kid => 
            (kid.authUserId || kid.auth_user_id) === savedStudentId
          );
        }
        
        // The savedStudentId is already the auth_user_id we need
        studentUserId = savedStudentId;
        console.log('🔍 QuestionSetGenerator: Using auth_user_id directly:', {
          savedStudentId,
          foundStudent: !!student,
          studentName: student?.name || student?.displayName,
          resolvedUserId: studentUserId
        });
      }
      
      if (!studentUserId) {
        console.error('No valid student user ID found. Please ensure a student is selected.');
        setLoading(false);
        return;
      }
      
      setSelectedStudentUserId(studentUserId);
      localStorage.setItem('selectedStudentUserId', studentUserId);
      
      const { data: profileData, error } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', studentUserId)
        .single();

      if (profileData) {
        setContextSummary(profileData.strategic_context_summary || '');
        setRefinementOptions(profileData.context_refinement_options || []);
        setQuestionSet(profileData.initial_question_set || []);
        setQuestionRefinementOptions(profileData.question_refinement_options || []);
        
        // Extract complete student profile (same logic as StudentContextGenerator)
        const studentData = familyUsers?.find(u => 
                             (u.authUserId || u.auth_user_id) === savedStudentId
                           ) || 
                           family?.kids?.find(kid => 
                             (kid.authUserId || kid.auth_user_id) === savedStudentId
                           );
        const profile = {
          // Basic Info
          name: studentData?.name || studentData?.displayName || 'Student',
          graduation_year: profileData.graduation_year || null,
          grade_level: profileData.grade_level || '',
          state_of_residency: profileData.state_of_residency || '',
          
          // Academic Performance
          gpaWeighted: profileData.gpa_weighted || null,
          gpa_unweighted: profileData.gpa_unweighted || null,
          gpa_scale: profileData.gpa_scale || null,
          class_rank: profileData.class_rank || null,
          class_size: profileData.class_size || null,
          class_rank_percentage: profileData.class_rank_percentage || null,
          
          // Test Scores
          satTotal: profileData.sat_total || null,
          sat_math: profileData.sat_math || null,
          sat_reading_writing: profileData.sat_reading_writing || null,
          actComposite: profileData.act_composite || null,
          act_math: profileData.act_math || null,
          act_english: profileData.act_english || null,
          act_reading: profileData.act_reading || null,
          act_science: profileData.act_science || null,
          psat_total: profileData.psat_total || null,
          national_merit_status: profileData.national_merit_status || '',
          
          // Academic Rigor
          curriculum_rigor: profileData.curriculum_rigor || '',
          ap_courses_taken: profileData.ap_courses_taken || null,
          ap_courses_planned: profileData.ap_courses_planned || null,
          ap_exams_passed: profileData.ap_exams_passed || null,
          ap_courses_details: profileData.ap_courses_details || '',
          dual_enrollment_credits: profileData.dual_enrollment_credits || null,
          honors_courses: profileData.honors_courses || null,
          course_load_description: profileData.course_load_description || '',
          ib_diploma_candidate: profileData.ib_diploma_candidate || false,
          ib_predicted_score: profileData.ib_predicted_score || null,
          
          // Academics & Interests
          academicInterests: Array.isArray(profileData.academic_interests) ? profileData.academic_interests : [],
          academic_interests: Array.isArray(profileData.academic_interests) ? profileData.academic_interests : [],
          careerGoals: profileData.career_goals || '',
          career_goals: profileData.career_goals || '',
          learningStyle: profileData.learning_style || '',
          learning_style: profileData.learning_style || '',
          academicStrengths: Array.isArray(profileData.academic_strengths) ? profileData.academic_strengths : [],
          academic_strengths: Array.isArray(profileData.academic_strengths) ? profileData.academic_strengths : [],
          uniqueQualities: profileData.unique_qualities || '',
          unique_qualities: profileData.unique_qualities || '',
          intendedMajor: profileData.intended_major || '',
          intended_major: profileData.intended_major || '',
          intended_minor: profileData.intended_minor || '',
          
          // Activities & Leadership (both JSONB and legacy text fields)
          extracurriculars: Array.isArray(profileData.extracurriculars) ? profileData.extracurriculars : [],
          leadership_activities: profileData.leadership_activities || '',
          work_experience: profileData.work_experience || '',
          volunteer_work: profileData.volunteer_work || '',
          
          // Awards & Recognition (both JSONB and legacy text fields)
          awards: Array.isArray(profileData.awards) ? profileData.awards : [],
          academic_awards: profileData.academic_awards || '',
          // High School Context
          hs_competitiveness: profileData.hs_competitiveness || '',
          hs_avg_test_scores: profileData.hs_avg_test_scores || '',
          hs_pct_college_bound: profileData.hs_pct_college_bound || null,
          
          // Grade Context
          grade_trend: profileData.grade_trend || '',
          grade_context: profileData.grade_context || '',
          special_talents: profileData.special_talents || '',
          
          // Background & Context
          high_school_name: profileData.high_school_name || '',
          high_school_type: profileData.high_school_type || '',
          high_school_city: profileData.high_school_city || '',
          high_school_state: profileData.high_school_state || '',
          special_circumstances: profileData.special_circumstances || '',
          first_generation_college: profileData.first_generation_college || false,
          legacy_connections: Array.isArray(profileData.legacy_connections) ? profileData.legacy_connections : [],
          underrepresented_background: profileData.underrepresented_background || false,
          
          // Additional Fields
          languages: profileData.languages || '',
          additional_notes: profileData.additional_notes || '',
          student_self_assessment: profileData.student_self_assessment || '',
          college_preferences: profileData.college_preferences || null
        };
        setStudentProfile(profile);

        // Extract parent A observations
        const parentA = {
          easySubject: profileData.parent_observation_easy_subject || '',
          growth: profileData.parent_observation_growth || '',
          learningDifference: profileData.parent_observation_learning_difference || profileData.parent_observation_poor_fit || '',
          surprises: profileData.parent_observation_surprises || '',
          ageLimit: profileData.parent_observation_age_limit || '',
          rulesQuestioned: profileData.parent_observation_rules_questioned || '',
          wrongLevel: profileData.parent_observation_wrong_level || '',
          advocacySuccess: profileData.parent_observation_advocacy_success || '',
          initiativeAgainstAdvice: profileData.parent_observation_initiative_against_advice || '',
          mechanicalSkills: profileData.parent_observation_mechanical_skills || '',
          mathIntegration: profileData.parent_observation_math_integration || '',
          entrepreneurialInstincts: profileData.parent_observation_entrepreneurial_instincts || '',
          selfTaught: profileData.parent_observation_self_taught || '',
          obsessions: profileData.parent_observation_obsessions || '',
          unusualInterests: profileData.parent_observation_unusual_interests || '',
          hiddenQualities: profileData.parent_observation_hidden_qualities || ''
        };
        setParentObservations(parentA);

        // Extract parent B observations
        const parentB = {
          easySubject: profileData.parent_b_easy_subject || '',
          growth: profileData.parent_b_growth || '',
          learningDifference: profileData.parent_b_learning_difference || profileData.parent_b_poor_fit || '',
          surprises: profileData.parent_b_surprises || '',
          ageLimit: profileData.parent_b_age_limit || '',
          rulesQuestioned: profileData.parent_b_rules_questioned || '',
          wrongLevel: profileData.parent_b_wrong_level || '',
          advocacySuccess: profileData.parent_b_advocacy_success || '',
          initiativeAgainstAdvice: profileData.parent_b_initiative_against_advice || '',
          mechanicalSkills: profileData.parent_b_mechanical_skills || '',
          mathIntegration: profileData.parent_b_math_integration || '',
          entrepreneurialInstincts: profileData.parent_b_entrepreneurial_instincts || '',
          obsessions: profileData.parent_b_obsessions || '',
          unusualInterests: profileData.parent_b_unusual_interests || '',
          hiddenQualities: profileData.parent_b_hidden_qualities || '',
          differentPerspective: profileData.parent_b_different_perspective || '',
          whatOtherParentMisses: profileData.parent_b_what_other_parent_misses || ''
        };
        setParentBObservations(parentB);

        // Load personality profile if available
        if (profileData.personality_profile) {
          try {
            const savedProfile = JSON.parse(profileData.personality_profile);
            setPersonalityProfile(savedProfile);
            console.log('🔍 QuestionSetGenerator: Loaded personality profile:', savedProfile);
          } catch (error) {
            console.log('Error parsing personality profile:', error);
            setPersonalityProfile({});
          }
        }

        // Load academic focus and positioning
        setAcademicFocus({
          focusArea: profileData.assessment_focus_area || 'engineering',
          positioningNarrative: profileData.positioning_narrative || 'institutional_catalyst',
          assessmentTemplate: profileData.assessment_template || 'engineering_institutional_catalyst'
        });
        console.log('🔍 QuestionSetGenerator: Loaded academic focus:', {
          focusArea: profileData.assessment_focus_area,
          positioningNarrative: profileData.positioning_narrative,
          assessmentTemplate: profileData.assessment_template
        });
        
        console.log('✅ Loaded complete profile data:', profileData);
      }
    } catch (error) {
      console.error('Error loading Phase 2 data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get human-readable labels for personality profile values
  const getPersonalityLabel = (value, category) => {
    const labels = {
      motivation: {
        'achievement': 'wants to excel and be acknowledged',
        'curiosity': 'driven to figure things out and learn',
        'impact': 'motivated by making a difference'
      },
      communication: {
        'data-logic': 'responds to facts, evidence, and logical reasoning',
        'stories-examples': 'learns through narratives and concrete examples',
        'visual-hands-on': 'needs to see it or try it to understand'
      },
      resistance: {
        'authority-skeptical': 'questions "because I said so" reasoning',
        'perfectionist-paralyzed': 'afraid of not doing it perfectly',
        'overwhelm-avoidant': 'shuts down when there\'s too much at once'
      },
      energy: {
        'competition': 'thrives on winning and outperforming others',
        'collaboration': 'energized by working with and helping others',
        'independent-exploration': 'needs space to figure things out alone'
      },
      decision: {
        'research-heavy': 'wants all the data before deciding',
        'intuition-based': 'goes with gut feeling and instinct',
        'consensus-seeking': 'wants input from trusted people'
      },
      feedback: {
        'direct-analytical': 'straightforward, data-based critique',
        'supportive-encouraging': 'positive reinforcement first',
        'challenge-based': 'framed as problems to solve'
      },
      identity: {
        'individual-achiever': 'personal excellence and recognition',
        'community-contributor': 'part of something bigger',
        'system-innovator': 'someone who improves how things work'
      }
    };

    return labels[category]?.[value] || value;
  };

  const generateQuestionPrompt = () => {
    const hasParentA = Object.values(parentObservations).some(val => val?.trim());
    const hasParentB = Object.values(parentBObservations).some(val => val?.trim());
    
    const prompt = `Create 8 foundational assessment questions based on this comprehensive student data:

**STRATEGIC CONTEXT:**
${contextSummary}

**REFINEMENT OPTIONS:**
${refinementOptions.map((option, index) => `${index + 1}. ${option}`).join('\n')}

**COMPLETE STUDENT PROFILE:**
Name: ${studentProfile?.name || 'Student'}
${studentProfile?.graduation_year ? `Graduation Year: ${studentProfile.graduation_year}` : ''}
${studentProfile?.state_of_residency ? `State: ${studentProfile.state_of_residency}` : ''}

**ACADEMIC PERFORMANCE:**
${studentProfile?.gpaWeighted ? `GPA Weighted: ${studentProfile.gpaWeighted}` : ''}
${studentProfile?.gpa_unweighted ? `GPA Unweighted: ${studentProfile.gpa_unweighted}` : ''}
${studentProfile?.gpa_scale ? `GPA Scale: ${studentProfile.gpa_scale}` : ''}
${studentProfile?.class_rank && studentProfile?.class_size ? `Class Rank: ${studentProfile.class_rank} of ${studentProfile.class_size}` : ''}
${studentProfile?.class_rank_percentage ? `Class Rank Percentile: ${studentProfile.class_rank_percentage}%` : ''}

**TEST SCORES:**
${studentProfile?.satTotal ? `SAT Total: ${studentProfile.satTotal}` : ''}
${studentProfile?.sat_math ? `SAT Math: ${studentProfile.sat_math}` : ''}
${studentProfile?.sat_reading_writing ? `SAT Reading/Writing: ${studentProfile.sat_reading_writing}` : ''}
${studentProfile?.actComposite ? `ACT Composite: ${studentProfile.actComposite}` : ''}
${studentProfile?.act_math ? `ACT Math: ${studentProfile.act_math}` : ''}
${studentProfile?.act_english ? `ACT English: ${studentProfile.act_english}` : ''}
${studentProfile?.act_reading ? `ACT Reading: ${studentProfile.act_reading}` : ''}
${studentProfile?.act_science ? `ACT Science: ${studentProfile.act_science}` : ''}
${studentProfile?.psat_total ? `PSAT Total: ${studentProfile.psat_total}` : ''}
${studentProfile?.national_merit_status ? `National Merit Status: ${studentProfile.national_merit_status}` : ''}

**ACADEMIC RIGOR:**
${studentProfile?.curriculum_rigor ? `Curriculum Rigor: ${studentProfile.curriculum_rigor}` : ''}
${studentProfile?.ap_courses_taken ? `AP Courses Taken: ${studentProfile.ap_courses_taken}` : ''}
${studentProfile?.ap_courses_planned ? `AP Courses Planned: ${studentProfile.ap_courses_planned}` : ''}
${studentProfile?.ap_exams_passed ? `AP Exams Passed: ${studentProfile.ap_exams_passed}` : ''}
${studentProfile?.ap_courses_details ? `AP Course Details: ${studentProfile.ap_courses_details}` : ''}
${studentProfile?.ib_diploma_candidate ? `IB Diploma Candidate: ${studentProfile.ib_diploma_candidate ? 'Yes' : 'No'}` : ''}
${studentProfile?.ib_predicted_score ? `IB Predicted Score: ${studentProfile.ib_predicted_score}` : ''}
${studentProfile?.dual_enrollment_credits ? `Dual Enrollment Credits: ${studentProfile.dual_enrollment_credits}` : ''}
${studentProfile?.honors_courses ? `Honors Courses: ${studentProfile.honors_courses}` : ''}
${studentProfile?.course_load_description ? `Course Load Description: ${studentProfile.course_load_description}` : ''}

**ACADEMICS & INTERESTS:**
Academic Interests: ${studentProfile?.academicInterests && Array.isArray(studentProfile.academicInterests) ? studentProfile.academicInterests.join(', ') : studentProfile?.academic_interests && Array.isArray(studentProfile.academic_interests) ? studentProfile.academic_interests.join(', ') : 'Not specified'}
Career Goals: ${studentProfile?.careerGoals || studentProfile?.career_goals || 'Not specified'}
Learning Style: ${studentProfile?.learningStyle || studentProfile?.learning_style || 'Not specified'}
Academic Strengths: ${studentProfile?.academicStrengths && Array.isArray(studentProfile.academicStrengths) ? studentProfile.academicStrengths.join(', ') : studentProfile?.academic_strengths && Array.isArray(studentProfile.academic_strengths) ? studentProfile.academic_strengths.join(', ') : 'Not specified'}
Unique Qualities: ${studentProfile?.uniqueQualities || studentProfile?.unique_qualities || 'Not specified'}
Intended Major: ${studentProfile?.intendedMajor || studentProfile?.intended_major || 'Not specified'}
${studentProfile?.intended_minor ? `Intended Minor: ${studentProfile.intended_minor}` : ''}

**ACTIVITIES & LEADERSHIP:**
${studentProfile?.extracurriculars && Array.isArray(studentProfile.extracurriculars) && studentProfile.extracurriculars.length > 0 ? `Extracurriculars: ${studentProfile.extracurriculars.map(activity => `${activity.name || activity} (${activity.category || 'Other'})`).join(', ')}` : ''}
${studentProfile?.leadership_activities ? `Leadership: ${studentProfile.leadership_activities}` : ''}
${studentProfile?.work_experience ? `Work Experience: ${studentProfile.work_experience}` : ''}
${studentProfile?.volunteer_work ? `Volunteer Work: ${studentProfile.volunteer_work}` : ''}

**AWARDS & RECOGNITION:**
${studentProfile?.awards && Array.isArray(studentProfile.awards) && studentProfile.awards.length > 0 ? `Awards: ${studentProfile.awards.map(award => award.name || award).join(', ')}` : ''}
${studentProfile?.academic_awards ? `Academic Awards: ${studentProfile.academic_awards}` : ''}
${studentProfile?.hs_competitiveness ? `High School Competitiveness: ${studentProfile.hs_competitiveness}` : ''}
${studentProfile?.hs_avg_test_scores ? `School Average Test Scores: ${studentProfile.hs_avg_test_scores}` : ''}
${studentProfile?.hs_pct_college_bound ? `% College Bound: ${studentProfile.hs_pct_college_bound}%` : ''}
${studentProfile?.grade_trend ? `Grade Trend: ${studentProfile.grade_trend}` : ''}
${studentProfile?.grade_context ? `Grade Context: ${studentProfile.grade_context}` : ''}
${studentProfile?.special_talents ? `Special Talents: ${studentProfile.special_talents}` : ''}

**BACKGROUND & CONTEXT:**
${studentProfile?.high_school_name ? `High School: ${studentProfile.high_school_name}` : ''}
${studentProfile?.high_school_city && studentProfile?.high_school_state ? `School Location: ${studentProfile.high_school_city}, ${studentProfile.high_school_state}` : ''}
${studentProfile?.high_school_type ? `School Type: ${studentProfile.high_school_type}` : ''}
${studentProfile?.grade_level ? `Grade Level: ${studentProfile.grade_level}` : ''}
${studentProfile?.special_circumstances ? `Special Circumstances: ${studentProfile.special_circumstances}` : ''}
${studentProfile?.first_generation_college !== undefined ? `First Generation College: ${studentProfile.first_generation_college ? 'Yes' : 'No'}` : ''}
${studentProfile?.legacy_connections && Array.isArray(studentProfile.legacy_connections) && studentProfile.legacy_connections.length > 0 ? `Legacy Connections: ${studentProfile.legacy_connections.join(', ')}` : ''}
${studentProfile?.underrepresented_background !== undefined ? `Underrepresented Background: ${studentProfile.underrepresented_background ? 'Yes' : 'No'}` : ''}
${studentProfile?.languages ? `Languages: ${studentProfile.languages}` : ''}
${studentProfile?.student_self_assessment ? `Student Self-Assessment: ${studentProfile.student_self_assessment}` : ''}
${studentProfile?.additional_notes ? `Additional Notes: ${studentProfile.additional_notes}` : ''}

**PERSONALITY PROFILE:**
${personalityProfile?.motivationStyle ? `Primary Motivation: ${personalityProfile.motivationStyle} - ${getPersonalityLabel(personalityProfile.motivationStyle, 'motivation')}` : ''}
${personalityProfile?.communicationPreference ? `Communication Style: ${personalityProfile.communicationPreference} - ${getPersonalityLabel(personalityProfile.communicationPreference, 'communication')}` : ''}
${personalityProfile?.resistancePatterns ? `Resistance Patterns: ${personalityProfile.resistancePatterns} - ${getPersonalityLabel(personalityProfile.resistancePatterns, 'resistance')}` : ''}
${personalityProfile?.energySource ? `Energy Source: ${personalityProfile.energySource} - ${getPersonalityLabel(personalityProfile.energySource, 'energy')}` : ''}
${personalityProfile?.decisionMaking ? `Decision Making: ${personalityProfile.decisionMaking} - ${getPersonalityLabel(personalityProfile.decisionMaking, 'decision')}` : ''}
${personalityProfile?.feedbackStyle ? `Feedback Preference: ${personalityProfile.feedbackStyle} - ${getPersonalityLabel(personalityProfile.feedbackStyle, 'feedback')}` : ''}
${personalityProfile?.identityFramework ? `Identity Framework: ${personalityProfile.identityFramework} - ${getPersonalityLabel(personalityProfile.identityFramework, 'identity')}` : ''}

**ACADEMIC FOCUS & POSITIONING:**
${academicFocus?.focusArea ? `Academic Focus: ${academicFocus.focusArea}` : ''}
${academicFocus?.positioningNarrative ? `Positioning Narrative: ${academicFocus.positioningNarrative}` : ''}
${academicFocus?.assessmentTemplate ? `Assessment Framework: ${academicFocus.assessmentTemplate}` : ''}

${hasParentA ? `**PARENT A OBSERVATIONS:**
${Object.entries(parentObservations)
  .filter(([_, value]) => value?.trim())
  .map(([key, value]) => `${key.replace(/([A-Z])/g, ' $1').toLowerCase()}: ${value}`)
  .join('\n')}` : ''}

${hasParentB ? `**PARENT B OBSERVATIONS:**
${Object.entries(parentBObservations)
  .filter(([_, value]) => value?.trim())
  .map(([key, value]) => `${key.replace(/([A-Z])/g, ' $1').toLowerCase()}: ${value}`)
  .join('\n')}` : ''}

**INSTRUCTIONS:**
Create exactly 8 questions that:
1. Build on this strategic positioning and academic focus framework
2. Are designed to reveal specific patterns that support the strategic narrative
3. Feel natural and conversational (not like interview questions)
4. Are tailored to the student's personality profile (motivation style, communication preference, etc.)
5. Are open-ended to allow detailed responses
6. Will provide rich material for dynamic follow-up questions
7. Each question should target different aspects of the student's profile
8. Consider the student's positioning narrative (${academicFocus?.positioningNarrative || 'institutional_catalyst'}) when framing questions
9. Align with their communication style: ${personalityProfile?.communicationPreference ? getPersonalityLabel(personalityProfile.communicationPreference, 'communication') : 'adapt to student preference'}
10. Match their motivation style: ${personalityProfile?.motivationStyle ? getPersonalityLabel(personalityProfile.motivationStyle, 'motivation') : 'consider individual motivation'}

**OUTPUT FORMAT:**
QUESTION_SET:
1. [First question - focus on natural strengths/capabilities, tailored to their ${personalityProfile?.communicationPreference || 'communication'} style]
2. [Second question - focus on problem-solving/innovation, aligned with ${academicFocus?.focusArea || 'academic'} interests]
3. [Third question - focus on values/character, considering their ${personalityProfile?.identityFramework || 'identity'} framework]
4. [Fourth question - focus on unique experiences/perspectives, leveraging ${academicFocus?.positioningNarrative || 'positioning'} narrative]
5. [Fifth question - focus on growth/learning patterns, matching their ${personalityProfile?.motivationStyle || 'motivation'} style]
6. [Sixth question - focus on impact/leadership potential, aligned with positioning as ${academicFocus?.positioningNarrative || 'leader'}]
7. [Seventh question - focus on intellectual curiosity/passion, tailored to ${academicFocus?.focusArea || 'academic'} focus]
8. [Eighth question - focus on future vision/goals, considering their ${personalityProfile?.decisionMaking || 'decision-making'} approach]

REFINEMENT_OPTIONS:
1. [Refinement for ${academicFocus?.focusArea || 'academic'}/intellectual positioning]
2. [Refinement for personal/character positioning based on ${personalityProfile?.identityFramework || 'identity'} framework]
3. [Refinement for ${academicFocus?.positioningNarrative || 'leadership'}/impact positioning]
4. [Refinement for unique differentiator positioning leveraging personality insights]

Generate questions that feel like genuine curiosity about who this student is, customized to their personality and academic focus.`;

    return prompt;
  };

  const copyPromptToClipboard = async () => {
    const prompt = generateQuestionPrompt();
    try {
      await navigator.clipboard.writeText(prompt);
      setCopySuccess('copied');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopySuccess('failed');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `q${questionSet.length + 1}`,
      question: '',
      focus: '',
      placeholder: ''
    };
    setQuestionSet([...questionSet, newQuestion]);
    setEditingQuestion(questionSet.length);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questionSet];
    updated[index] = { ...updated[index], [field]: value };
    setQuestionSet(updated);
  };

  const removeQuestion = (index) => {
    const updated = questionSet.filter((_, i) => i !== index);
    setQuestionSet(updated);
    if (editingQuestion === index) {
      setEditingQuestion(null);
    }
  };

  const generateDynamicQuestionRefinements = () => {
    // Generate refinement options based on the current question set
    const refinements = [
      'Adjust questions to emphasize academic intellectual curiosity and depth',
      'Refine questions to highlight character development and personal values',
      'Modify questions to showcase leadership potential and collaborative skills',
      'Enhance questions to reveal unique perspectives and differentiating experiences'
    ];
    setQuestionRefinementOptions(refinements);
  };

  const parseBulkQuestions = (text) => {
    // Parse pasted text and extract questions
    const questions = [];
    
    // Handle both simple numbered lists and complex format with categories and quotes
    const lines = text.split('\n');
    let currentQuestion = '';
    let inQuote = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines and headers
      if (!line || line === 'QUESTION_SET:') continue;
      
      // Handle multi-line quoted questions
      if (line.includes('"')) {
        if (line.match(/^".*"$/)) {
          // Complete question on one line
          const question = line.replace(/"/g, '').trim();
          if (question.length > 10) {
            questions.push(question);
          }
        } else if (line.startsWith('"')) {
          // Start of multi-line question
          inQuote = true;
          currentQuestion = line.replace(/^"/, '').trim();
        } else if (line.endsWith('"') && inQuote) {
          // End of multi-line question
          currentQuestion += ' ' + line.replace(/"$/, '').trim();
          if (currentQuestion.length > 10) {
            questions.push(currentQuestion);
          }
          currentQuestion = '';
          inQuote = false;
        } else if (inQuote) {
          // Middle of multi-line question
          currentQuestion += ' ' + line.trim();
        }
      } else if (inQuote) {
        // Continue collecting multi-line question
        currentQuestion += ' ' + line.trim();
      } else if (line.match(/^\d+[\.\)]\s*/)) {
        // Handle various numbered formats: "1. Text" or "1) Text"
        const cleaned = line.replace(/^\d+[\.\)]\s*/, '').trim();
        
        // Check if this looks like a category label vs actual question
        const isCategoryLabel = (
          // Short phrases that are likely categories
          cleaned.length < 80 &&
          // Common category patterns
          (cleaned.match(/^(Natural|Problem|Values|Unique|Growth|Impact|Intellectual|Future|Academic|Personal|Leadership|Character|Innovation|Learning|Vision|Experience|Perspective|Curiosity|Passion|Goal)/i) ||
           cleaned.match(/(Strengths|Capabilities|Solving|Character|Experiences|Perspectives|Patterns|Potential|Goals|Vision)$/i) ||
           // Slash-separated categories like "Problem-Solving/Innovation"
           cleaned.match(/\w+\/\w+/) ||
           // Categories with slashes or hyphens
           cleaned.match(/^[\w\s]+[\/\-][\w\s]+$/) ||
           // Single word or very short phrase categories
           cleaned.split(' ').length <= 3)
        );
        
        // Only add if it's not a category label and is substantial
        if (!isCategoryLabel && cleaned && cleaned.length > 15) {
          questions.push(cleaned);
        }
      }
      // Skip category lines that start with numbers and have brackets: "1. [Category]"
    }
    
    // Handle case where quote doesn't close properly
    if (inQuote && currentQuestion.length > 10) {
      questions.push(currentQuestion);
    }
    
    return questions;
  };

  const applyBulkPaste = () => {
    const parsedQuestions = parseBulkQuestions(bulkPasteText);
    console.log('🔍 Parsed questions:', parsedQuestions);
    console.log('🔍 Number of questions found:', parsedQuestions.length);
    
    // Debug: Show each parsed question
    parsedQuestions.forEach((q, i) => {
      console.log(`🔍 Question ${i + 1}:`, q);
      console.log(`🔍 Question ${i + 1} length:`, q.length);
      console.log(`🔍 Question ${i + 1} type:`, typeof q);
    });
    
    if (parsedQuestions.length > 0) {
      // Convert string array to question objects
      const questionObjects = parsedQuestions.map((questionText, index) => ({
        id: `q${index + 1}`,
        question: questionText,
        focus: '',
        placeholder: ''
      }));
      
      console.log('🔍 Question objects created:', questionObjects);
      console.log('🔍 First question object:', questionObjects[0]);
      console.log('🔍 Current questionSet before update:', questionSet);
      
      setQuestionSet(questionObjects);
      setBulkPasteText('');
      setShowBulkPaste(false);
      console.log(`✅ Applied ${parsedQuestions.length} questions from bulk paste`);
      
      // Debug after state update (use setTimeout to see after state updates)
      setTimeout(() => {
        console.log('🔍 QuestionSet after update (in setTimeout):', questionObjects);
      }, 100);
    } else {
      console.log('⚠️ No valid questions found in pasted text');
    }
  };

  const saveQuestionSet = async () => {
    console.log('🔍 DEBUG: saveQuestionSet called');
    console.log('🔍 DEBUG: questionSet.length:', questionSet.length);
    console.log('🔍 DEBUG: questionSet content:', questionSet);
    console.log('🔍 DEBUG: selectedStudentUserId:', selectedStudentUserId);
    console.log('🔍 DEBUG: currentUser:', currentUser);
    
    if (!currentUser || questionSet.length < 8 || !selectedStudentUserId) {
      console.log('🚨 DEBUG: Save blocked - validation failed');
      console.log('🚨 DEBUG: currentUser exists:', !!currentUser);
      console.log('🚨 DEBUG: questionSet.length >= 8:', questionSet.length >= 8);
      console.log('🚨 DEBUG: selectedStudentUserId exists:', !!selectedStudentUserId);
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        initial_question_set: questionSet,
        question_refinement_options: questionRefinementOptions,
        assessment_phase: 3,
        updated_at: new Date().toISOString()
      };
      
      console.log('🔍 DEBUG: Updating database with:', updateData);
      
      const { error } = await supabase
        .from('student_academic_profiles')
        .update(updateData)
        .eq('user_id', selectedStudentUserId);

      if (error) {
        console.error('Error saving question set:', error);
      } else {
        // Update master strategy
        await updateMasterStrategy({
          initial_question_set: questionSet,
          question_refinement_options: questionRefinementOptions,
          phase_3_completed: true,
          updated_at: new Date().toISOString()
        });

        console.log('✅ Question set saved successfully');
        console.log('✅ DEBUG: Data saved to database successfully');
        
        // Navigate to Phase 4
        navigate('/college/dynamic-guidance-generator');
      }
    } catch (error) {
      console.error('Error saving question set:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading strategic context</h2>
          <p className="text-slate-600">Preparing question generation...</p>
        </div>
      </div>
    );
  }

  if (!contextSummary) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Brain className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Strategic Context Required</h2>
          <p className="text-slate-600 mb-6">
            You need to complete Phase 2 (Strategic Context Generation) before creating assessment questions.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/college/student-context-generator')}
              className="w-full px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium"
            >
              Complete Phase 2: Strategic Context
            </button>
            <button
              onClick={() => navigate('/college')}
              className="w-full px-6 py-3 text-slate-600 hover:text-slate-900"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/college/student-context-generator')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Strategic Context
            </button>
          </div>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-full border border-slate-700 mb-4">
              <MessageCircle className="w-5 h-5 text-white" />
              <span className="text-sm font-medium text-white">Phase 3: Question Set</span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Initial Question Set Generator
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Create the foundational 8 questions (Questions 1-8) that will strategically position your student 
              and set up dynamic follow-up questions.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {/* Strategic Context Review */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Strategic Context (Phase 2)
            </h2>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="text-slate-700 text-sm leading-relaxed">
                {contextSummary}
              </p>
            </div>

            {refinementOptions.length > 0 && (
              <div>
                <h3 className="font-medium text-slate-900 mb-2">Refinement Options:</h3>
                <ul className="space-y-1">
                  {refinementOptions.map((option, index) => (
                    <li key={index} className="text-sm text-slate-600">
                      {index + 1}. {option}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Step 1: Generate Question Prompt */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5" />
              Step 1: Copy Prompt for Browser Claude
            </h2>
            
            <p className="text-slate-600 mb-4">
              Copy this prompt and paste it into browser Claude to generate strategic assessment questions 
              based on your student's context.
            </p>

            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Question Generation Prompt</span>
                <button
                  onClick={copyPromptToClipboard}
                  className={`flex items-center gap-2 px-3 py-1 rounded text-sm font-medium ${
                    copySuccess === 'copied' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : copySuccess === 'failed'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                >
                  {copySuccess === 'copied' ? (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      Copy Prompt
                    </>
                  )}
                </button>
              </div>
              <div className="bg-white rounded border p-3 max-h-64 overflow-y-auto">
                <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                  {generateQuestionPrompt()}
                </pre>
              </div>
            </div>
          </div>

          {/* Step 2: Edit Question Set */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Step 2: Create Your Question Set (Questions 1-8)
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowBulkPaste(!showBulkPaste)}
                  className="flex items-center gap-2 px-3 py-1 bg-azure-100 text-azure-700 rounded hover:bg-azure-200 text-sm"
                >
                  <FileText className="w-4 h-4" />
                  Bulk Paste
                </button>
                <button
                  onClick={addQuestion}
                  className="flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Question
                </button>
              </div>
            </div>
            
            <p className="text-slate-600 mb-6">
              Edit the questions Claude generated, or create your own strategic questions. 
              These 8 questions will be the foundation of your student's assessment.
            </p>

            {/* Bulk Paste Section */}
            {showBulkPaste && (
              <div className="mb-6 p-4 bg-azure-50 rounded-lg border border-azure-200">
                <h3 className="font-medium text-azure-900 mb-2">Bulk Paste Questions</h3>
                <p className="text-sm text-azure-700 mb-3">
                  Paste your question set below. Each question should be on a new line. Numbers and bullets will be automatically removed.
                </p>
                <textarea
                  value={bulkPasteText}
                  onChange={(e) => setBulkPasteText(e.target.value)}
                  placeholder='QUESTION_SET:
1. [Category]
"What situation or challenge has most shaped how you think about problems?"
2. [Category]
"Tell me about a time when you disagreed with the conventional approach to something..."

OR simple format:
1. What situation has shaped how you think?
2. Tell me about disagreeing with convention...'
                  className="w-full h-32 px-3 py-2 border border-azure-300 rounded focus:ring-2 focus:ring-azure-500 focus:border-azure-500 text-sm"
                />
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={() => {
                      setShowBulkPaste(false);
                      setBulkPasteText('');
                    }}
                    className="px-3 py-1 text-azure-600 hover:text-azure-800 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={applyBulkPaste}
                    disabled={!bulkPasteText.trim()}
                    className="px-4 py-1 bg-azure-600 text-white rounded hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Apply Questions
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {questionSet.map((question, index) => {
                console.log(`🔍 Rendering question ${index + 1}:`, question);
                console.log(`🔍 Question text for ${index + 1}:`, question.question);
                return (
                <div key={index} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-slate-900 text-white rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <span className="text-sm font-medium text-slate-700">Question {index + 1}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingQuestion(editingQuestion === index ? null : index)}
                        className="p-1 text-slate-400 hover:text-slate-600"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeQuestion(index)}
                        className="p-1 text-slate-400 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {editingQuestion === index ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Question Text</label>
                        <textarea
                          value={question.question}
                          onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                          placeholder="Enter the assessment question..."
                          className="w-full px-3 py-2 border border-slate-200 rounded focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                          rows="3"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Focus Area</label>
                        <input
                          type="text"
                          value={question.focus}
                          onChange={(e) => updateQuestion(index, 'focus', e.target.value)}
                          placeholder="e.g., natural strengths, problem-solving, values..."
                          className="w-full px-3 py-2 border border-slate-200 rounded focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Placeholder Text</label>
                        <input
                          type="text"
                          value={question.placeholder}
                          onChange={(e) => updateQuestion(index, 'placeholder', e.target.value)}
                          placeholder="Helpful prompt for students..."
                          className="w-full px-3 py-2 border border-slate-200 rounded focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-slate-900 mb-2">{question.question || 'Click edit to add question text'}</p>
                      {question.focus && (
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">
                          <Target className="w-3 h-3" />
                          {question.focus}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            {questionSet.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No questions yet. Add questions using the button above or paste from Claude.</p>
              </div>
            )}
          </div>

          {/* Question Refinement Options */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Lightbulb className="w-5 h-5" />
                Question Refinement Options
              </h2>
              <button
                onClick={generateDynamicQuestionRefinements}
                className="flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
              >
                <RefreshCw className="w-3 h-3" />
                Generate Options
              </button>
            </div>
            
            <div className="space-y-2">
              {[0, 1, 2, 3].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={questionRefinementOptions[index] || ''}
                  onChange={(e) => {
                    const newOptions = [...questionRefinementOptions];
                    newOptions[index] = e.target.value;
                    setQuestionRefinementOptions(newOptions);
                  }}
                  placeholder={`Refinement option ${index + 1} for questions...`}
                  className="w-full px-3 py-2 border border-slate-200 rounded focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                />
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/college/student-context-generator')}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Strategic Context
            </button>

            <button
              onClick={saveQuestionSet}
              disabled={questionSet.length < 8 || saving}
              className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save & Continue to Phase 4
                </>
              )}
            </button>
          </div>

          {questionSet.length < 8 && (
            <div className="text-center">
              <p className="text-slate-500 text-sm">
                You need at least 8 questions to proceed to Phase 4
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionSetGenerator;